"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.CoreFeaturesProviderForSSR = exports.CoreFeaturesProvider = exports.fetchFeatureFlagAndExperimentAssignments = exports.CoreFeaturesContext = void 0;
const react_1 = __importDefault(require("react"));
const types_1 = require("./types");
exports.CoreFeaturesContext = react_1.default.createContext({
    isLoading: true,
    getFeatureFlagAssignment: () => undefined,
    getExperimentAssignment: () => undefined,
});
function getFeatureFlagCriteria(flagsArray, businessToken, userGuid, targetedRunTime, businessTargetingProperties) {
    return flagsArray.map((flag) => {
        let subjectTypeTargetingPropertyConfig;
        const subjectTypeOverride = (0, types_1.isFeatureFlagWithSubjectType)(flag)
            ? flag.subjectTypeOverride
            : undefined;
        const subjectKey = (0, types_1.isFeatureFlagWithSubjectType)(flag) ? flag.key : flag;
        if (subjectTypeOverride === 'user_guid') {
            subjectTypeTargetingPropertyConfig = {
                user: {
                    legacyGuid: userGuid,
                },
            };
        }
        else {
            const extraProperties = businessTargetingProperties || {};
            subjectTypeTargetingPropertyConfig = {
                business: {
                    token: businessToken,
                    ...extraProperties,
                },
            };
        }
        return {
            featureKey: subjectKey,
            subjectKey: subjectTypeOverride === 'user_guid' ? userGuid : businessToken,
            targetingProperties: {
                ...subjectTypeTargetingPropertyConfig,
                runtime: targetedRunTime,
            },
        };
    });
}
function getExperimentsCriteria(experimentsArray, businessGuid, userGuid, targetedRunTime, businessTargetingProperties) {
    return experimentsArray.map((exp) => {
        let subjectTypeTargetingPropertyConfig;
        if (exp.subjectTypeOverride === 'user_guid') {
            subjectTypeTargetingPropertyConfig = {
                user: {
                    legacyGuid: userGuid,
                },
            };
        }
        else {
            const extraProperties = businessTargetingProperties || {};
            subjectTypeTargetingPropertyConfig = {
                business: {
                    legacyGuid: businessGuid,
                    ...extraProperties,
                },
            };
        }
        return {
            featureKey: exp.key,
            subjectKey: exp.subjectTypeOverride === 'user_guid' ? userGuid : businessGuid,
            targetingProperties: {
                ...subjectTypeTargetingPropertyConfig,
                runtime: targetedRunTime,
            },
        };
    });
}
function getAssignmentValue(assignment) {
    let result = assignment.stringValue ||
        assignment.numericValue ||
        assignment.booleanValue;
    if (result === undefined) {
        result = assignment.defaultValue;
    }
    return result;
}
function fetchAssignments(postSender, criteria, hydrateTargetingProperties) {
    /** hydrateTargetingProperties is used for non-SSR clients that want the BFF
     * to hydrate targeting properties for them.
     */
    return postSender(`/v1/features/client/evaluate${hydrateTargetingProperties ? '?hydrateTargetingProperties=true' : ''}`, {
        criteria,
    });
}
// eslint-disable-next-line sonarjs/cognitive-complexity
async function fetchFeatureFlagAndExperimentAssignments({ businessGuid, userGuid, businessToken, experiments, featureFlags, postSender, targetedRuntime, businessTargetingProperties, hydrateTargetingProperties = false, }) {
    const featureFlagAssignments = {};
    const experimentStateMap = {};
    // Convert the feature flags and experiments to arrays so that we can
    // iterate over them
    const flagsArray = Object.values(featureFlags);
    const flagsKeysArray = flagsArray.map((flag) => (0, types_1.isFeatureFlagWithSubjectType)(flag) ? flag.key : flag);
    const experimentsArray = Object.values(experiments);
    try {
        const ffCriteria = getFeatureFlagCriteria(flagsArray, businessToken, userGuid, targetedRuntime, businessTargetingProperties);
        const experimentCriteria = getExperimentsCriteria(experimentsArray, businessGuid, userGuid, targetedRuntime, businessTargetingProperties);
        const response = await fetchAssignments(postSender, [...ffCriteria, ...experimentCriteria], hydrateTargetingProperties);
        const assignments = response.data.assignments;
        for (const assignment of assignments) {
            const assignmentValue = getAssignmentValue(assignment);
            if (flagsKeysArray.includes(assignment.featureKey)) {
                featureFlagAssignments[assignment.featureKey] = assignmentValue;
            }
            else {
                const experiment = experimentsArray.find((exp) => exp.key === assignment.featureKey);
                if (!experiment) {
                    console.error(`Received assignment for unknown experiment: ${assignment.featureKey}`);
                    continue;
                }
                experimentStateMap[experiment.key] = {
                    assignmentVariantName: assignment.assignmentKey,
                    assignmentValue: assignmentValue || 'control',
                    subjectType: experiment.subjectTypeOverride || 'business_guid',
                    subjectKey: experiment.subjectTypeOverride === 'user_guid'
                        ? userGuid
                        : businessGuid,
                    shouldReportAssignment: assignmentValue !== null,
                    isReported: false,
                };
            }
        }
    }
    catch (error) {
        console.error('Failed to fetch feature flag assignments', error);
        flagsArray.forEach((flag) => {
            const flagKey = (0, types_1.isFeatureFlagWithSubjectType)(flag) ? flag.key : flag;
            featureFlagAssignments[flagKey] = false;
        });
        experimentsArray.forEach((exp) => {
            experimentStateMap[exp.key] = {
                assignmentVariantName: 'control',
                assignmentValue: 'control',
                subjectType: exp.subjectTypeOverride || 'business_guid',
                subjectKey: exp.subjectTypeOverride === 'user_guid' ? userGuid : businessGuid,
                /**
                 * Need to report so that users who were previously assigned
                 * somethign different from control will be invalidated in data
                 */
                shouldReportAssignment: true,
                isReported: false,
            };
        });
    }
    return { featureFlagAssignments, experimentStateMap };
}
exports.fetchFeatureFlagAndExperimentAssignments = fetchFeatureFlagAndExperimentAssignments;
function reportAssignment(experimentKey, experimentState, assignmentReporter) {
    if (experimentState.shouldReportAssignment && !experimentState.isReported) {
        const assignment = {
            experimentKey,
            subjectType: experimentState.subjectType,
            subjectKey: experimentState.subjectKey,
            assignmentVariantName: experimentState.assignmentVariantName || '',
            assignmentValue: experimentState.assignmentValue,
        };
        try {
            assignmentReporter(assignment);
        }
        catch (error) {
            console.error('Failed to report assignment. This is very bad and should be investigated immediately. The experiment could be invalidated.', `Experiment key: ${experimentKey}`, error);
        }
        experimentState.isReported = true;
    }
}
function CoreFeaturesProvider({ children, businessToken, businessGuid, userGuid, featureFlags, experiments, postSender, assignmentReporter, targetedRuntime, }) {
    const [isLoading, setIsLoading] = react_1.default.useState(true);
    // Using useRef instead of useState so that only the loading state will
    // cause rerender
    const featureFlagAssignments = react_1.default.useRef({});
    const experimentStateMap = react_1.default.useRef({});
    react_1.default.useEffect(() => {
        setIsLoading(true);
        fetchFeatureFlagAndExperimentAssignments({
            businessGuid,
            userGuid,
            businessToken,
            experiments,
            featureFlags,
            postSender,
            targetedRuntime,
            hydrateTargetingProperties: true,
        })
            .then(({ featureFlagAssignments: newFeatureFlagAssignments, experimentStateMap: newExperimentStateMap, }) => {
            featureFlagAssignments.current = newFeatureFlagAssignments;
            experimentStateMap.current = newExperimentStateMap;
        })
            .finally(() => setIsLoading(false));
    }, [
        businessGuid,
        userGuid,
        businessToken,
        experiments,
        featureFlags,
        postSender,
        targetedRuntime,
    ]);
    const getFeatureFlagAssignment = (flagKey) => {
        if (featureFlagAssignments.current[flagKey] === undefined && !isLoading) {
            console.error(`Missing data for feature flag: ${flagKey}. Make sure it is defined in the feature flags config.`);
        }
        return featureFlagAssignments.current[flagKey];
    };
    const getExperimentAssignment = (experimentKey) => {
        const experimentState = experimentStateMap.current[experimentKey];
        if (!experimentState) {
            if (!isLoading) {
                console.error(`Missing data for experiment: ${experimentKey}. Make sure it is defined in the experiments config.`);
            }
            return undefined;
        }
        reportAssignment(experimentKey, experimentState, assignmentReporter);
        return experimentState.assignmentValue;
    };
    return (react_1.default.createElement(exports.CoreFeaturesContext.Provider, { value: {
            isLoading,
            getFeatureFlagAssignment,
            getExperimentAssignment,
        } }, children));
}
exports.CoreFeaturesProvider = CoreFeaturesProvider;
/**
 * Use this provider if you are server-side rendering the app.
 * Use fetchFeatureFlagAndExperimentAssignments to fetch backend
 * data during server-side rendering, and pass the object it returns
 * to this provider, as coreFeaturesBackendData.
 */
function CoreFeaturesProviderForSSR({ children, coreFeaturesBackendData, assignmentReporter, }) {
    const { featureFlagAssignments, experimentStateMap } = coreFeaturesBackendData;
    const getFeatureFlagAssignment = (flagKey) => {
        if (featureFlagAssignments[flagKey] === undefined) {
            console.error(`Missing data for feature flag: ${flagKey}. Make sure it is defined in the feature flags config.`);
        }
        return featureFlagAssignments[flagKey];
    };
    const getExperimentAssignment = (experimentKey) => {
        const experimentState = experimentStateMap[experimentKey];
        if (!experimentState) {
            console.error(`Missing data for experiment: ${experimentKey}. Make sure it is defined in the experiments config.`);
            return undefined;
        }
        reportAssignment(experimentKey, experimentState, assignmentReporter);
        return experimentState.assignmentValue;
    };
    return (react_1.default.createElement(exports.CoreFeaturesContext.Provider, { value: {
            isLoading: false,
            getFeatureFlagAssignment,
            getExperimentAssignment,
        } }, children));
}
exports.CoreFeaturesProviderForSSR = CoreFeaturesProviderForSSR;
